import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiFetchJson } from "../apiClient";
import { useAuth } from "../AuthContext";

const ROLES = ["user", "admin"];
const PERMISSIONS = ["run", "edit"];

/** Who an agent or workflow is shared with: roles, groups (owners: their own groups;
 * administrators: any group) and — for administrators — individual users by e-mail.
 * `accessPath` is the item's `/access` endpoint; saving replaces the whole rule list. */
export default function SharingPanel({ accessPath }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rules, setRules] = useState([]);
  const [saved, setSaved] = useState("[]");
  const [groups, setGroups] = useState([]);
  const [draft, setDraft] = useState({ type: "role", id: "user", email: "", permission: "run" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await apiFetchJson(accessPath);
      setRules(data.rules || []);
      setSaved(JSON.stringify(data.rules || []));
      setError("");
    } catch (e) {
      setError(e.message);
    }
  }, [accessPath]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiFetchJson("/api/v1/agents/sharing/groups")
      .then((data) => setGroups(data.items || []))
      .catch(() => setGroups([]));       // no groups to offer; roles still work
  }, []);

  const add = () => {
    const rule = draft.type === "user"
      ? { type: "user", email: draft.email.trim(), id: "", label: draft.email.trim(), permission: draft.permission }
      : { type: draft.type, id: draft.id, permission: draft.permission,
          label: draft.type === "group" ? (groups.find((g) => g.id === draft.id) || {}).display_name : draft.id };
    if (!(rule.id || rule.email)) return;
    setRules((prev) => [...prev.filter((r) => !(r.type === rule.type && (r.id || r.email) === (rule.id || rule.email))), rule]);
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = rules.map(({ type, id, email, permission }) => (id ? { type, id, permission } : { type, email, permission }));
      const data = await apiFetchJson(accessPath, { method: "PUT", body: JSON.stringify({ rules: body }) });
      setRules(data.rules || []);
      setSaved(JSON.stringify(data.rules || []));
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const types = isAdmin ? ["role", "group", "user"] : ["role", "group"];
  const changed = JSON.stringify(rules) !== saved;

  return (
    <section className="ds-card ac-form-stack" aria-label={t("agents.sharingTitle")}>
      <h3 className="ac-detail-heading">{t("agents.sharingTitle")}</h3>
      <p className="ds-help">{t("agents.sharingHelp")}</p>
      {error && <div className="ds-alert ds-alert--danger" role="alert">{error}</div>}
      {rules.length === 0 ? <p className="ds-meta">{t("agents.sharingNone")}</p> : (
        <ul className="ds-list">
          {rules.map((r) => (
            <li key={`${r.type}:${r.id || r.email}`} className="ds-list-row ac-toolbar-flush">
              <span className="ds-badge">{t(`agents.sharingType_${r.type}`)}</span>
              <span className="ac-grow">{r.label || r.id || r.email}</span>
              <select className="ds-select" aria-label={t("agents.sharingPermission")} value={r.permission}
                      onChange={(e) => setRules((prev) => prev.map((p) => (p === r ? { ...p, permission: e.target.value } : p)))}>
                {PERMISSIONS.map((p) => <option key={p} value={p}>{t(`agents.sharingPermission_${p}`)}</option>)}
              </select>
              <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm"
                      onClick={() => setRules((prev) => prev.filter((p) => p !== r))}>{t("agents.sharingRemove")}</button>
            </li>
          ))}
        </ul>
      )}
      <div className="ds-toolbar ac-toolbar-flush">
        <select className="ds-select" aria-label={t("agents.sharingType")} value={draft.type}
                onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value,
                  id: e.target.value === "role" ? "user" : e.target.value === "group" ? (groups[0] || {}).id || "" : "" }))}>
          {types.map((type) => <option key={type} value={type}>{t(`agents.sharingType_${type}`)}</option>)}
        </select>
        {draft.type === "role" && (
          <select className="ds-select" aria-label={t("agents.sharingType_role")} value={draft.id}
                  onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))}>
            {ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
          </select>
        )}
        {draft.type === "group" && (
          <select className="ds-select" aria-label={t("agents.sharingType_group")} value={draft.id}
                  onChange={(e) => setDraft((d) => ({ ...d, id: e.target.value }))}>
            {groups.length === 0 && <option value="">{t("agents.sharingNoGroups")}</option>}
            {groups.map((g) => <option key={g.id} value={g.id}>{g.display_name || g.id}</option>)}
          </select>
        )}
        {draft.type === "user" && (
          <input className="ds-input" type="email" aria-label={t("agents.sharingEmail")}
                 placeholder={t("agents.sharingEmail")} value={draft.email}
                 onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} />
        )}
        <select className="ds-select" aria-label={t("agents.sharingPermission")} value={draft.permission}
                onChange={(e) => setDraft((d) => ({ ...d, permission: e.target.value }))}>
          {PERMISSIONS.map((p) => <option key={p} value={p}>{t(`agents.sharingPermission_${p}`)}</option>)}
        </select>
        <button type="button" className="ds-btn ds-btn--secondary ds-btn--sm" onClick={add}>{t("agents.sharingAdd")}</button>
      </div>
      <div className="ac-actions">
        <button type="button" className="ds-btn ds-btn--primary" disabled={busy || !changed} onClick={save}>
          {t("agents.sharingSave")}
        </button>
      </div>
    </section>
  );
}
